import { appConfig } from "src/config";
import { toSha256 } from "../cryptography";
import { getAuthorizationHeader } from "src/api/utils";
import { getActiveUser } from "@noom/hive-auth";

const S3_FILE_SALT = "s3file";

// Image upload constraints
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_WIDTH = 800;
const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const JPEG_QUALITY_STEPS = [0.9, 0.8, 0.7];

interface SignedS3UrlResponse {
  signedS3Url: string;
}

interface S3UploadData {
  signedUrl: string;
  filePath: string;
}

/**
 * Checks if the file type is allowed
 * @param type - The MIME type to check
 * @returns true if allowed, false otherwise
 */
function isAllowedType(type: string): boolean {
  return ALLOWED_TYPES.has(type);
}

/**
 * Loads an image file into an HTMLImageElement
 * @param file - The image file to load
 * @returns Promise resolving to the loaded image element
 */
async function readImageElement(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Draws image to canvas, downscaled to max width while preserving aspect ratio
 * @param img - The image element to draw
 * @param maxWidth - Maximum width in pixels
 * @returns Canvas with downscaled image
 */
function drawDownscaledToCanvas(
  img: HTMLImageElement,
  maxWidth: number
): HTMLCanvasElement {
  const scale = Math.min(1, maxWidth / img.naturalWidth);
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

/**
 * Compresses canvas to JPEG, trying different quality levels to stay under max size
 * @param canvas - The canvas to compress
 * @param maxBytes - Maximum file size in bytes
 * @returns Promise resolving to compressed JPEG blob
 */
async function compressToJpegUnder(
  canvas: HTMLCanvasElement,
  maxBytes: number
): Promise<Blob> {
  // eslint-disable-next-line no-await-in-loop
  for (const q of JPEG_QUALITY_STEPS) {
    // eslint-disable-next-line no-await-in-loop
    const blob: Blob = await new Promise((res, rej) =>
      canvas.toBlob(
        (b) => (b ? res(b) : rej(new Error("Failed to convert to blob"))),
        "image/jpeg",
        q
      )
    );
    if (blob.size <= maxBytes) return blob;
  }
  // return last attempt even if larger
  const lastQuality = JPEG_QUALITY_STEPS[JPEG_QUALITY_STEPS.length - 1];
  return new Promise((res, rej) =>
    canvas.toBlob(
      (b) => (b ? res(b) : rej(new Error("Failed to convert to blob"))),
      "image/jpeg",
      lastQuality
    )
  );
}

/**
 * Validates and processes an image file for upload
 * Validates type and size, downscales to max width, converts to JPEG, and strips EXIF
 * @param file - The original image file
 * @returns Promise resolving to a processed File object (JPEG format)
 * @throws Error if file type or size is invalid
 */
async function preprocessForUpload(file: File): Promise<File> {
  if (!isAllowedType(file.type)) {
    throw new Error(
      `File type "${file.type}" is not supported. Please use JPEG, PNG, or WebP images.`
    );
  }
  if (file.size > MAX_BYTES) {
    throw new Error(
      `File size (${(file.size / 1024 / 1024).toFixed(
        1
      )}MB) exceeds maximum allowed size of ${MAX_BYTES / 1024 / 1024}MB`
    );
  }

  const img = await readImageElement(file);
  const canvas = drawDownscaledToCanvas(img, MAX_WIDTH);
  const blob = await compressToJpegUnder(canvas, MAX_BYTES);
  const processed = new File([blob], "profile.jpg", { type: "image/jpeg" });
  return processed;
}

/**
 * Requests a presigned S3 URL from the Coach Server for uploading a profile photo.
 * Generates a unique file path using hashed access code, timestamp, and UUID.
 *
 * @returns Promise containing the presigned URL and the S3 file path
 * @throws Error if access token is missing or if the server request fails
 */
export async function getSignedS3UrlForProfilePhotoUpload(): Promise<S3UploadData> {
  const activeUser = getActiveUser();
  if (!activeUser) {
    throw new Error("Authentication required for profile photo upload");
  }

  // Concatenate accessCode + salt and hash with SHA-256
  const hashedAccessCode = await toSha256(
    `${activeUser.accessCode}${S3_FILE_SALT}`
  );

  // Generate timestamp in format: YYYY-MM-DD-HH-MM-SS
  const now = new Date();
  const timestamp = now
    .toISOString()
    .replace(/T/, "-")
    .replace(/\..+/, "")
    .replace(/:/g, "-");

  // Generate UUID
  const uuid = crypto.randomUUID();

  // Construct file path
  const filePath = `groups/profile-pictures/${hashedAccessCode}-${timestamp}-${uuid}.jpg`;

  const body = {
    bucketName: appConfig.PROFILE_IMAGE_S3_BUCKET_NAME,
    filePath,
  };

  // Call Coach Server directly (similar to chat endpoints pattern)
  const response = await fetch(
    `${appConfig.COACH_SERVER_URL}/aws/signedS3Url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthorizationHeader(),
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to get signed S3 URL: ${response.status}`);
  }

  const responseData: SignedS3UrlResponse = await response.json();
  return {
    signedUrl: responseData.signedS3Url,
    filePath,
  };
}

/**
 * Uploads an image file directly to S3 using a presigned URL
 * @param file - The image file to upload
 * @param signedS3Url - The presigned S3 URL
 */
export async function uploadImageFileToS3(
  file: File,
  signedS3Url: string
): Promise<void> {
  const response = await fetch(signedS3Url, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to upload profile photo to S3: ${response.status} ${response.statusText}`
    );
  }
}

/**
 * Attaches a profile photo URL to the user's profile on the Coach Server.
 * This updates the user's profile with the S3 URL of their uploaded photo.
 *
 * @param profilePictureUrl - The full S3 URL of the uploaded profile photo
 * @param userData - User data containing name, age, and gender
 * @returns Promise resolving to the fetch response
 * @throws Error if access token is missing or if the server request fails
 */
export async function attachPhotoToUser(
  profilePictureUrl: string,
  userData: {
    name: string;
    age: string | number;
    gender: string;
  }
) {
  const accessCode = getActiveUser()?.accessCode;
  if (!accessCode) {
    throw new Error("Authentication is required to attach photo to user");
  }

  // Build query parameters with required fields
  const params = new URLSearchParams({
    accessCode,
    profilePictureUrl,
    name: userData.name,
    age: String(userData.age),
    gender: userData.gender,
  });

  // Call Coach Server to attach photo to user profile (POST with empty body)
  const response = await fetch(
    `${
      appConfig.COACH_SERVER_URL
    }/users/${accessCode}/profile?${params.toString()}`,
    {
      method: "POST",
      headers: {
        accept: "*/*",
        ...getAuthorizationHeader(),
      },
      body: "",
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to attach photo to user profile: ${response.status} ${response.statusText}`
    );
  }

  return response;
}

/**
 * Constructs the full S3 URL from a file path
 * @param filePath - The S3 file path
 * @returns The full S3 URL
 */
export function getS3Url(filePath: string): string {
  return `https://s3.amazonaws.com/${appConfig.PROFILE_IMAGE_S3_BUCKET_NAME}/${filePath}`;
}

/**
 * Uploads an image file to S3 and returns the S3 URL
 * This is a convenience function that combines validation, processing, getting the presigned URL, and uploading
 * @param file - The image file to upload
 * @returns The full S3 URL of the uploaded image
 * @throws Error if file validation fails or upload fails
 */
export async function uploadProfilePhotoToS3AndGetUrl(
  file: File
): Promise<string> {
  // Step 1: Validate and process image (validate type/size, downscale, convert to JPEG, strip EXIF)
  const processedFile = await preprocessForUpload(file);

  // Step 2: Get signed S3 URL and file path
  const { signedUrl, filePath } = await getSignedS3UrlForProfilePhotoUpload();

  // Step 3: Upload the processed file to S3
  await uploadImageFileToS3(processedFile, signedUrl);

  // Step 4: Return the full S3 URL
  return getS3Url(filePath);
}
